import type { Game } from '../game/Game';
import { hashBrownDoneness, hashBrownProgress, waffleStatus } from '../game/kitchen';
import { TOPPING_INFO } from '../game/lingo';
import { TOPPINGS, type EggStyle, type Topping, type WaffleKind } from '../game/types';
import { IRON_H, IRON_W, SLOT_H, SLOT_W, drawGrillSlot, drawIron } from '../render/food';
import { ctx2d } from '../render/sprite';
import { ticketColor } from '../render/theme';
import { h, onPress, onTapOrHold, setAttr, setText, toggleClass } from './dom';
import { ICONS, iconImg } from './iconImages';

export interface KitchenActions {
  tapSlot(i: number): void;
  tossSlot(i: number): void;
  tapIron(i: number): void;
  tossIron(i: number): void;
  topping(t: Topping): void;
  drop(): void;
  waffle(kind: WaffleKind): void;
  eggs(style: EggStyle): void;
  bacon(): void;
}

export const SLOT_KEYS = ['Z', 'X', 'C', 'V'] as const;
export const IRON_KEYS = ['B', 'N'] as const;
export const LINE_KEYS = {
  drop: 'Q',
  classic: 'W',
  pecan: 'E',
  scrambled: 'R',
  overEasy: 'T',
  bacon: 'Y',
} as const;

interface SlotView {
  btn: HTMLButtonElement;
  ctx: CanvasRenderingContext2D;
  tag: HTMLElement;
  well: HTMLElement;
  label: HTMLElement;
  needle: HTMLElement;
  zoneGolden: HTMLElement;
  zoneCrispy: HTMLElement;
}

interface IronView {
  btn: HTMLButtonElement;
  ctx: CanvasRenderingContext2D;
  tag: HTMLElement;
  label: HTMLElement;
  fill: HTMLElement;
}

function keyHint(key: string): HTMLElement {
  return h('kbd', { class: 'key' }, key);
}

/** The line: flat-top grill slots, waffle irons, topping buttons and the rest of the menu. */
export class Kitchen {
  readonly el: HTMLElement;
  private readonly slots: SlotView[] = [];
  private readonly irons: IronView[] = [];
  private readonly toppingButtons = new Map<Topping, HTMLButtonElement>();
  private readonly lineButtons: Record<keyof typeof LINE_KEYS, HTMLButtonElement>;
  private time = 0;

  constructor(actions: KitchenActions) {
    const grill = h('div', { class: 'grill', 'aria-label': 'Flat-top grill' });
    for (let i = 0; i < 4; i++) {
      const canvas = h('canvas', { class: 'slot__canvas', width: SLOT_W, height: SLOT_H });
      const tag = h('span', { class: 'slot__tag' });
      const well = h('span', { class: 'slot__well', hidden: true }, 'WELL');
      const label = h('span', { class: 'slot__label' }, 'EMPTY');
      const zoneGolden = h('i', { class: 'meter__zone meter__zone--golden' });
      const zoneCrispy = h('i', { class: 'meter__zone meter__zone--crispy' });
      const needle = h('i', { class: 'meter__needle' });
      const btn = h(
        'button',
        { class: 'slot', type: 'button', 'data-state': 'empty' },
        canvas,
        tag,
        well,
        label,
        h('div', { class: 'meter' }, zoneGolden, zoneCrispy, needle),
        keyHint(SLOT_KEYS[i] ?? ''),
      );
      onTapOrHold(
        btn,
        () => actions.tapSlot(i),
        () => actions.tossSlot(i),
      );
      grill.append(btn);
      this.slots.push({
        btn,
        ctx: ctx2d(canvas),
        tag,
        well,
        label,
        needle,
        zoneGolden,
        zoneCrispy,
      });
    }

    const ironsEl = h('div', { class: 'irons', 'aria-label': 'Waffle irons' });
    for (let i = 0; i < 2; i++) {
      const canvas = h('canvas', { class: 'iron__canvas', width: IRON_W, height: IRON_H });
      const tag = h('span', { class: 'slot__tag' });
      const label = h('span', { class: 'slot__label' }, 'EMPTY');
      const fill = h('i', { class: 'meter__fill' });
      const btn = h(
        'button',
        { class: 'iron', type: 'button', 'data-state': 'empty' },
        canvas,
        tag,
        label,
        h('div', { class: 'meter meter--iron' }, fill),
        keyHint(IRON_KEYS[i] ?? ''),
      );
      onTapOrHold(
        btn,
        () => actions.tapIron(i),
        () => actions.tossIron(i),
      );
      ironsEl.append(btn);
      this.irons.push({ btn, ctx: ctx2d(canvas), tag, label, fill });
    }

    const toppings = h('div', { class: 'toppings', 'aria-label': 'Hash brown toppings' });
    for (const t of TOPPINGS) {
      const info = TOPPING_INFO[t];
      const btn = h(
        'button',
        {
          class: 'btn btn--topping',
          type: 'button',
          'data-topping': t,
          'aria-label': `${info.lingo} (${info.ingredient})`,
        },
        iconImg(ICONS.topping(t), 'btn__icon'),
        h('span', { class: 'btn__label' }, info.lingo),
        h('span', { class: 'btn__sub' }, info.ingredient),
        h('span', { class: 'btn__check', 'aria-hidden': 'true' }, '✓'),
        keyHint(info.key),
      );
      onPress(btn, () => actions.topping(t));
      toppings.append(btn);
      this.toppingButtons.set(t, btn);
    }

    const mk = (
      key: keyof typeof LINE_KEYS,
      icon: HTMLImageElement,
      label: string,
      sub: string,
      fn: () => void,
    ): HTMLButtonElement => {
      const btn = h(
        'button',
        { class: `btn btn--line btn--${key}`, type: 'button', 'aria-label': `${label} ${sub}` },
        icon,
        h('span', { class: 'btn__label' }, label),
        h('span', { class: 'btn__sub' }, sub),
        keyHint(LINE_KEYS[key]),
      );
      onPress(btn, fn);
      return btn;
    };
    this.lineButtons = {
      drop: mk('drop', iconImg(ICONS.hashBrowns(), 'btn__icon'), 'DROP', 'hash browns', () =>
        actions.drop(),
      ),
      classic: mk(
        'classic',
        iconImg(ICONS.waffle('classic'), 'btn__icon'),
        'WAFFLE',
        'plain batter',
        () => actions.waffle('classic'),
      ),
      pecan: mk(
        'pecan',
        iconImg(ICONS.waffle('pecan'), 'btn__icon'),
        'PECAN',
        'waffle batter',
        () => actions.waffle('pecan'),
      ),
      scrambled: mk(
        'scrambled',
        iconImg(ICONS.eggs('scrambled'), 'btn__icon'),
        'SCRAMBLED',
        'mark eggs',
        () => actions.eggs('scrambled'),
      ),
      overEasy: mk(
        'overEasy',
        iconImg(ICONS.eggs('overEasy'), 'btn__icon'),
        'OVER EASY',
        'mark eggs',
        () => actions.eggs('overEasy'),
      ),
      bacon: mk('bacon', iconImg(ICONS.bacon(), 'btn__icon'), 'BACON', 'pull meat', () =>
        actions.bacon(),
      ),
    };
    const line = h(
      'div',
      { class: 'line', 'aria-label': 'The rest of the menu' },
      ...Object.values(this.lineButtons),
    );

    this.el = h(
      'section',
      { class: 'kitchen' },
      h('div', { class: 'stations' }, grill, ironsEl),
      h('div', { class: 'controls' }, toppings, line),
    );
  }

  update(game: Game, dt: number, hints: boolean): void {
    this.time += dt;
    const active = game.activeTicket();

    game.grill.forEach((slot, i) => {
      const v = this.slots[i];
      if (!v) return;
      const t = game.ticket(slot.ticketId);
      const occupied = slot.ticketId !== null;
      const doneness = occupied ? hashBrownDoneness(slot.time, game.cook) : null;
      const progress = hashBrownProgress(slot.time, game.cook);
      drawGrillSlot(v.ctx, {
        occupied,
        progress,
        toppings: slot.toppings,
        seed: i * 97 + (slot.ticketId ?? 0) * 13,
        time: this.time,
        burnt: doneness === 'burnt',
        selected: t !== undefined && t.id === game.activeTicketId,
      });
      const state = doneness ?? 'empty';
      setAttr(v.btn, 'data-state', state);
      const well = t?.order.hashBrowns?.well ?? false;
      const target = doneness === (well ? 'crispy' : 'golden');
      toggleClass(v.btn, 'is-target', target);
      toggleClass(v.btn, 'is-active-ticket', t !== undefined && t.id === game.activeTicketId);
      v.btn.style.setProperty('--tc', t ? ticketColor(t.color) : 'transparent');
      setText(v.tag, t ? `#${t.no}` : '');
      v.well.hidden = !(occupied && well);
      const firstEmpty = game.grill.findIndex((g) => g.ticketId === null) === i;
      const needsDrop =
        firstEmpty &&
        active?.order.hashBrowns &&
        !active.plated.hashBrowns &&
        !game.slotFor(active.id);
      setText(
        v.label,
        !occupied
          ? needsDrop
            ? 'TAP: DROP'
            : 'EMPTY'
          : doneness === 'burnt'
            ? 'BURNT! TOSS'
            : target
              ? `${doneness?.toUpperCase()} ✓ PLATE`
              : `${doneness?.toUpperCase()}`,
      );
      v.needle.style.left = `${progress * 100}%`;
      const c = game.cook;
      v.zoneGolden.style.left = `${(c.golden / c.burnt) * 100}%`;
      v.zoneGolden.style.width = `${((c.crispy - c.golden) / c.burnt) * 100}%`;
      v.zoneCrispy.style.left = `${(c.crispy / c.burnt) * 100}%`;
      v.zoneCrispy.style.width = `${((c.burnt - c.crispy) / c.burnt) * 100}%`;
      toggleClass(v.zoneGolden, 'is-target', occupied && !well);
      toggleClass(v.zoneCrispy, 'is-target', occupied && well);
      setAttr(
        v.btn,
        'aria-label',
        occupied
          ? `Grill ${i + 1}: ticket ${t?.no ?? '?'} hash browns, ${state}. Tap to ${state === 'burnt' ? 'toss' : 'plate'}, hold to toss.`
          : `Grill ${i + 1}: empty`,
      );
    });

    game.irons.forEach((iron, i) => {
      const v = this.irons[i];
      if (!v) return;
      const t = game.ticket(iron.ticketId);
      const status = iron.ticketId === null ? 'empty' : waffleStatus(iron.time, game.cook);
      drawIron(v.ctx, {
        kind: iron.kind,
        status,
        progress: iron.time / game.cook.waffleReady,
        time: this.time,
        powered: game.powerOn,
      });
      const state = !game.powerOn && status !== 'ready' ? 'nopower' : status;
      setAttr(v.btn, 'data-state', state);
      toggleClass(v.btn, 'is-target', status === 'ready');
      toggleClass(v.btn, 'is-active-ticket', t !== undefined && t.id === game.activeTicketId);
      v.btn.style.setProperty('--tc', t ? ticketColor(t.color) : 'transparent');
      setText(v.tag, t ? `#${t.no}${iron.kind === 'pecan' ? ' PECAN' : ''}` : '');
      setText(
        v.label,
        state === 'nopower'
          ? 'NO POWER'
          : status === 'empty'
            ? 'EMPTY'
            : status === 'cooking'
              ? 'COOKING…'
              : status === 'ready'
                ? 'DING! PLATE'
                : 'BURNT! TOSS',
      );
      const fill =
        status === 'cooking' ? iron.time / game.cook.waffleReady : status === 'empty' ? 0 : 1;
      v.fill.style.transform = `scaleX(${Math.min(1, fill)})`;
    });

    // Buttons reflect what the active ticket still needs.
    const o = active?.order;
    const slot = active ? game.slotFor(active.id) : undefined;
    const hbOpen = !!o?.hashBrowns && !active?.plated.hashBrowns;
    for (const [t, btn] of this.toppingButtons) {
      const added = slot?.toppings.includes(t) ?? false;
      const needed = hbOpen && !!o?.hashBrowns?.toppings.includes(t);
      toggleClass(btn, 'is-added', added);
      toggleClass(btn, 'is-hint', hints && needed && !added);
      toggleClass(btn, 'is-wrong', added && !needed);
      setAttr(btn, 'aria-disabled', hbOpen ? null : 'true');
    }
    const lb = this.lineButtons;
    setAttr(lb.drop, 'aria-disabled', hbOpen && !slot ? null : 'true');
    toggleClass(lb.drop, 'is-hint', hints && hbOpen && !slot);
    const waffleOpen =
      !!o?.waffle && !active?.plated.waffle && !(active && game.ironFor(active.id));
    for (const kind of ['classic', 'pecan'] as const) {
      setAttr(lb[kind], 'aria-disabled', waffleOpen && game.powerOn ? null : 'true');
      toggleClass(lb[kind], 'is-hint', hints && waffleOpen && game.powerOn && o?.waffle === kind);
    }
    const eggsOpen =
      !!o?.eggs && !active?.plated.eggs && !(active && game.lineJobFor(active.id, 'eggs'));
    for (const style of ['scrambled', 'overEasy'] as const) {
      setAttr(lb[style], 'aria-disabled', eggsOpen ? null : 'true');
      toggleClass(lb[style], 'is-hint', hints && eggsOpen && o?.eggs === style);
    }
    const baconOpen =
      !!o?.bacon && !active?.plated.bacon && !(active && game.lineJobFor(active.id, 'bacon'));
    setAttr(lb.bacon, 'aria-disabled', baconOpen ? null : 'true');
    toggleClass(lb.bacon, 'is-hint', hints && baconOpen);
  }
}
